import React, { useState, useEffect } from 'react';
// import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { ArrowLeft, Loader2, ShieldAlert, Bell, Eye, TrendingUp, TrendingDown, Search, Filter } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
// import { createPageUrl } from '@/utils';
import { Toaster } from "sonner";
import { format } from "date-fns";
import { useAuth } from '../context/AuthContext';
// import TermsModal from '@/components/dashboard/TermsModal';
// import * as userApi from '@/components/api/userApi';
// import * as adminApi from '@/components/api/adminApi';

// -- Mock API placeholders until actual endpoints are available --
const adminApi = {
    getAllFolders: async () => [
        { folder_id: '1', name: 'Gold Scalp', folder_code: 'GOLD_SC' },
        { folder_id: '2', name: 'Major Pairs', folder_code: 'MAJORS' }
    ],
    getAllAlerts: async (filters) => {
        // Mock Data
        return [
            {
                alert_id: 1,
                signal: 'BUY',
                pair: 'XAUUSD',
                timeframe: 'M15',
                folder_code: 'GOLD_SC',
                entry: 2030.50,
                sl: 2025.00,
                created_date: new Date().toISOString(),
            },
            {
                alert_id: 2,
                signal: 'SELL',
                pair: 'EURUSD',
                timeframe: 'H1',
                folder_code: 'MAJORS',
                entry: 1.0850,
                sl: 1.0900,
                created_date: new Date(Date.now() - 3600000).toISOString(),
            }
        ].filter(item => {
            if (filters.signal && item.signal !== filters.signal) return false;
            if (filters.pair && !item.pair.toLowerCase().includes(filters.pair.toLowerCase())) return false;
            return true;
        });
    },
    getAlertById: async (id) => {
        return {
            alert_id: id,
            signal: 'BUY',
            pair: 'XAUUSD',
            folder_name: 'Gold Scalp',
            folder_code: 'GOLD_SC',
            folder_active: true,
            timeframe: 'M15',
            entry: 2030.50,
            sl: 2025.00,
            indicator: 'RSI Divergence',
            alert_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
            created_date: new Date().toISOString(),
            tp_json: [2035.00, 2040.00, 2050.00],
            text: "Strong bullish divergence detected on M15 timeframe. RSI oversold."
        };
    }
};

const userApi = {
    getTermsStatus: async (id) => ({ accepted: true, terms_version_id: 1, terms_content: "Terms here..." }),
    acceptTerms: async (id, version) => true
};
// ------------------------------------------------

export default function AdminAlerts() {
    const { user, loading: authLoading } = useAuth();
    const [userLoading, setUserLoading] = useState(true);
    const [termsAccepting, setTermsAccepting] = useState(false);
    const [termsStatus, setTermsStatus] = useState(null);
    const [filters, setFilters] = useState({ folder_id: '', signal: '', pair: '' });
    const [selectedAlertId, setSelectedAlertId] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        // Simulate user loading from existing context or API
        if (!authLoading && user) {
            setUserLoading(false);
            // Mock checking terms
            setTermsStatus({ accepted: true });
        } else if (!authLoading && !user) {
            setUserLoading(false);
        }
    }, [user, authLoading]);

    const hasAcceptedTerms = termsStatus?.accepted === true;
    const isAdmin = user?.role === 'admin';

    const { data: folders = [] } = useQuery({
        queryKey: ['adminFolders'],
        queryFn: () => adminApi.getAllFolders(),
        enabled: !!(hasAcceptedTerms && isAdmin),
    });

    const { data: alerts = [], isLoading } = useQuery({
        queryKey: ['adminAlerts', filters],
        queryFn: () => adminApi.getAllAlerts({
            folder_id: filters.folder_id || undefined,
            signal: filters.signal || undefined,
            pair: filters.pair || undefined,
        }),
        enabled: !!(hasAcceptedTerms && isAdmin),
    });

    const { data: alertDetail, isLoading: detailLoading } = useQuery({
        queryKey: ['adminAlertDetail', selectedAlertId],
        queryFn: () => adminApi.getAlertById(selectedAlertId),
        enabled: !!selectedAlertId && hasAcceptedTerms && isAdmin,
    });

    const acceptTerms = async () => {
        setTermsAccepting(true);
        // await userApi.acceptTerms(user.id, termsStatus.terms_version_id);
        setTermsStatus({ ...termsStatus, accepted: true });
        setTermsAccepting(false);
    };

    const openDetailDialog = (alertId) => {
        setSelectedAlertId(alertId);
        setDetailDialogOpen(true);
    };

    if (authLoading || userLoading) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
        );
    }

    // Terms Modal Placeholder - Simplified since TermsModal wasn't provided
    if (!hasAcceptedTerms) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center text-white">
                <div className="text-center">
                    <h2 className="text-xl mb-4">Terms Acceptance Required</h2>
                    <Button onClick={acceptTerms} disabled={termsAccepting}>
                        {termsAccepting ? "Accepting..." : "Accept Mock Terms"}
                    </Button>
                </div>
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-white mb-2">Access Denied</h1>
                    <p className="text-slate-400 mb-4">Only administrators can access this page.</p>
                    <Link to="/dashboard">
                        <Button className="bg-blue-600 hover:bg-blue-700">Go to Dashboard</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900 p-6">
            <Toaster richColors position="top-right" />

            <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-4 mb-8">

                    <div>
                        <h1 className="text-2xl font-bold text-white">Alert Monitoring</h1>
                        <p className="text-slate-400 text-sm mt-1">View all alerts in the system</p>
                    </div>
                </div>

                {/* Filters */}
                <Card className="bg-slate-950 border-slate-800 mb-6">
                    <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Filter className="h-4 w-4 text-slate-500" />
                                <span className="text-slate-400 text-sm">Filters:</span>
                            </div>

                            <Select
                                value={filters.folder_id}
                                onValueChange={(value) => setFilters({ ...filters, folder_id: value === 'all' ? '' : value })}
                            >
                                <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="All Folders" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                    <SelectItem value="all" className="text-slate-200 focus:bg-slate-700 focus:text-white">All Folders</SelectItem>
                                    {folders.map(f => (
                                        <SelectItem key={f.folder_id} value={f.folder_id} className="text-slate-200 focus:bg-slate-700 focus:text-white">{f.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>

                            <Select
                                value={filters.signal}
                                onValueChange={(value) => setFilters({ ...filters, signal: value === 'all' ? '' : value })}
                            >
                                <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-white">
                                    <SelectValue placeholder="All Signals" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                    <SelectItem value="all" className="text-slate-200 focus:bg-slate-700 focus:text-white">All Signals</SelectItem>
                                    <SelectItem value="BUY" className="text-slate-200 focus:bg-slate-700 focus:text-white">BUY</SelectItem>
                                    <SelectItem value="SELL" className="text-slate-200 focus:bg-slate-700 focus:text-white">SELL</SelectItem>
                                </SelectContent>
                            </Select>

                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <Input
                                    value={filters.pair}
                                    onChange={(e) => setFilters({ ...filters, pair: e.target.value })}
                                    placeholder="Search pair..."
                                    className="pl-9 w-40 bg-slate-800 border-slate-700 text-white"
                                />
                            </div>

                            {(filters.folder_id || filters.signal || filters.pair) && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setFilters({ folder_id: '', signal: '', pair: '' })}
                                    className="text-slate-400 hover:text-white"
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Alerts Table */}
                <Card className="bg-slate-950 border-slate-800">
                    <CardHeader className="border-b border-slate-800">
                        <div className="flex items-center gap-3">
                            <Bell className="h-5 w-5 text-blue-400" />
                            <CardTitle className="text-white">All Alerts ({alerts.length})</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-slate-800 hover:bg-transparent">
                                    <TableHead className="text-slate-400">Signal</TableHead>
                                    <TableHead className="text-slate-400">Pair</TableHead>
                                    <TableHead className="text-slate-400">Folder</TableHead>
                                    <TableHead className="text-slate-400">Entry</TableHead>
                                    <TableHead className="text-slate-400">SL</TableHead>
                                    <TableHead className="text-slate-400">Time</TableHead>
                                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                                            Loading alerts...
                                        </TableCell>
                                    </TableRow>
                                ) : alerts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                                            <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                                            No alerts found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    alerts.map(alert => (
                                        <TableRow key={alert.alert_id} className="border-slate-800">
                                            <TableCell>
                                                <Badge className={alert.signal === 'BUY'
                                                    ? 'bg-emerald-500 text-white'
                                                    : 'bg-red-500 text-white'
                                                }>
                                                    {alert.signal === 'BUY' ? (
                                                        <TrendingUp className="h-3 w-3 mr-1" />
                                                    ) : (
                                                        <TrendingDown className="h-3 w-3 mr-1" />
                                                    )}
                                                    {alert.signal}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-white font-medium">{alert.pair}</span>
                                                {alert.timeframe && (
                                                    <span className="text-slate-500 text-xs ml-2">{alert.timeframe}</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="border-slate-600 text-slate-300">
                                                    {alert.folder_code}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-slate-300">{alert.entry}</TableCell>
                                            <TableCell className="text-red-400">{alert.sl}</TableCell>
                                            <TableCell>
                                                <span className="text-slate-400 text-sm">
                                                    {format(new Date(alert.created_date), 'MMM d, HH:mm')}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openDetailDialog(alert.alert_id)}
                                                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            {/* Alert Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-white">Alert Details</DialogTitle>
                    </DialogHeader>
                    {detailLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                        </div>
                    ) : alertDetail ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-slate-500 text-sm">Signal</span>
                                    <div className="mt-1">
                                        <Badge className={alertDetail.signal === 'BUY'
                                            ? 'bg-emerald-500 text-white'
                                            : 'bg-red-500 text-white'
                                        }>
                                            {alertDetail.signal}
                                        </Badge>
                                    </div>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Pair</span>
                                    <p className="text-white font-medium">{alertDetail.pair}</p>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Folder</span>
                                    <p className="text-white">{alertDetail.folder_name} ({alertDetail.folder_code})</p>
                                    {!alertDetail.folder_active && (
                                        <Badge className="bg-red-500/20 text-red-400 text-xs mt-1">Folder Disabled</Badge>
                                    )}
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Timeframe</span>
                                    <p className="text-white">{alertDetail.timeframe || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Entry</span>
                                    <p className="text-white font-medium">{alertDetail.entry}</p>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Stop Loss</span>
                                    <p className="text-red-400 font-medium">{alertDetail.sl}</p>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Indicator</span>
                                    <p className="text-white">{alertDetail.indicator || '-'}</p>
                                </div>
                                <div>
                                    <span className="text-slate-500 text-sm">Alert Time</span>
                                    <p className="text-white">{alertDetail.alert_time || format(new Date(alertDetail.created_date), 'yyyy-MM-dd HH:mm:ss')}</p>
                                </div>
                            </div>

                            {alertDetail.tp_json && alertDetail.tp_json.length > 0 && (
                                <div>
                                    <span className="text-slate-500 text-sm">Take Profits</span>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {alertDetail.tp_json.map((tp, i) => (
                                            <Badge key={i} className="bg-emerald-500/20 text-emerald-400">
                                                TP{i + 1}: {tp}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {alertDetail.text && (
                                <div>
                                    <span className="text-slate-500 text-sm">Full Text</span>
                                    <ScrollArea className="h-40 mt-2">
                                        <pre className="text-slate-300 text-sm whitespace-pre-wrap bg-slate-800/50 p-4 rounded-lg">
                                            {alertDetail.text}
                                        </pre>
                                    </ScrollArea>
                                </div>
                            )}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
